import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getVendors } from "api/vendor";
import CustomReactSelect from "components/CustomReactSelect";
import BaseTable from "components/DataTable/BaseTable";
import ListingImageCol from "components/ListingImageCol";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import VendorCategorySelector from "components/VendorCategorySelector";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useFilterStore from "store/useFilterStore";
import { formatDateTime } from "utils/helper";

const TABLE_COLUMNS = [
    { id: "name", Header: "Name", accessor: "name", align: "left", sortable: true },
    { id: "category", Header: "Category", accessor: "category", align: "left" },
    { id: "contacts", Header: "Contacts", accessor: "contacts", align: "left" },
    { id: "is_parts_supplier", Header: "Part Supplier", accessor: "part_supplier", align: "left" },
    { id: "hourly_rate", Header: "Hourly Rate", accessor: "hourly_rate", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
];

export const parentRoute = '/vendors';

const PARTS_SUPPLIER = [
    { value: '', label: "All" },
    { value: true, label: "Yes" },
    { value: false, label: "No" }
]


function VendorTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const { getFilters, setFilters } = useFilterStore();
    const [refreshKey] = useState(0);
    const savedState = location.state || { sort: "created_at", dir: "desc" };
    const [params, setParams] = useState({});
    const currentPath = window.location.pathname;

    useEffect(() => {
        const record = getFilters(currentPath);
        if (record?.customParams) {
            setParams(record.customParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist custom filters whenever they change (merge with existing baseTable state)
    useEffect(() => {
        const record = getFilters(currentPath);
        setFilters(currentPath, {
            baseTable: record.baseTable, // preserve the table paging/sort managed by BaseTable
            customParams: params
        });
    }, [params, currentPath]);


    const createTableRow = useCallback((item) => ({
        name: <ListingImageCol imgUrl={item.img_url} name={item.name} />,
        category: <MDTypography component="div" variant="standard" color="text">{item.category ?? "-"}</MDTypography>,
        contacts: <MDTypography component="div" variant="standard" color="text">
            {item.contacts?.find(c => c.is_primary)?.name ?? "-"}<br />
            {item.contacts?.find(c => c.is_primary)?.phone ?? "-"}
        </MDTypography>,
        part_supplier: <MDTypography component="div" variant="standard" color="text">
            {item.is_parts_supplier ? 'Yes' : 'No'}
        </MDTypography>,
        hourly_rate: <MDTypography component="div" variant="standard" color="text">
            {item.hourly_rate ?? "-"}
        </MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>
    }), []);

    const fetchWithFilters = useCallback(async (filters = {}) => {
        const filterParams = Object.fromEntries(
            Object.entries({ ...filters, ...params }).filter(([key, value]) => value != null && value !== '')
        );
        return getVendors(filterParams);
    }, [params]);

    const filtersUI = (
        <>
            <MDBox sx={{ flex: 1.3, minWidth: 200 }}>
                <VendorCategorySelector
                    value={params?.category}
                    onChange={(selectedValues) => setParams(prev => ({ ...prev, category: selectedValues }))}
                />
            </MDBox>
            <MDBox >
                <MDTypography variant="caption" sx={{ mt: { xs: 0, sm: -1.5 }, mb: 0.5, display: "block" }}>
                    Hourly Rate (Min / Max)
                </MDTypography>
                <MDBox display="flex" gap={1}>
                    <MDInput
                        type="number"
                        placeholder="Min"
                        value={params?.hourly_rate_min ?? ""}
                        inputProps={{ min: 0 }}
                        onChange={e => {
                            const value = e.target.value ? Math.max(0, Number(e.target.value)) : null
                            setParams(prev => ({ ...prev, hourly_rate_min: value }));
                        }}
                        sx={{ width: 70 }}
                        size="small"
                    />
                    <MDInput
                        type="number"
                        placeholder="Max"
                        value={params?.hourly_rate_max ?? ""}
                        inputProps={{ min: 0 }}
                        onChange={e => {
                            const value = e.target.value ? Math.max(0, Number(e.target.value)) : null
                            setParams(prev => ({ ...prev, hourly_rate_max: value }));
                        }}
                        sx={{ width: 70 }}
                        size="small"
                    />
                </MDBox>
            </MDBox>
            <MDBox sx={{ flex: 1, minWidth: 100 }}>
                <CustomReactSelect
                    label="Parts Supplier"
                    name="is_parts_supplier"
                    placeholder="Parts Supplier"
                    value={PARTS_SUPPLIER.find(role => role.value === params?.is_parts_supplier)}
                    onChange={(selectedOption) => setParams(prev => ({ ...prev, is_parts_supplier: selectedOption?.value }))}
                    options={PARTS_SUPPLIER}
                    isClearable
                />
            </MDBox>
        </>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <BaseTable
                key={refreshKey}
                title="Vendors Management"
                columns={TABLE_COLUMNS}
                fetchData={fetchWithFilters}
                defaultState={savedState}
                createTableRow={createTableRow}
                showAddButton
                addButtonText="Add Vendors"
                onAddClick={() => navigate(`${parentRoute}/add-vendor`, { state: savedState })}
                showSearch
                searchPlaceholder="Search vendor name..."
                additionalFilters={filtersUI}
                onClickRow={(row, key) => { navigate(`${parentRoute}/${row.id}`, { state: savedState }) }}
            />
        </DashboardLayout>
    );
}

export default VendorTable;